/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { AspectRatio, HStack, Icon, Image, Stack, Text } from "@microbit/ui";
import { RiInformationLine } from "react-icons/ri";
import { FormattedMessage, useIntl } from "react-intl";
import makecodeBackImage from "./images/makecode-back.png";
import accelerometerImage from "./images/microbit_xyz_arrows.png";
import { ActionData, TourStep, TourTrigger, TourTriggerName } from "./model";

const FormattedMessageStepContent = ({ id }: { id: string }) => {
  return (
    <Text>
      <FormattedMessage id={id} />
    </Text>
  );
};

export const tourElClassname = {
  liveGraph: "live-graph",
  dataSamplesActionCard: "data-samples-action-card",
  recordDataSamplesCard: "record-data-samples-card",
  addActionButton: "add-action-button",
  trainModelButton: "train-model-button",
  estimatedAction: "estimated-action",
  certaintyThreshold: "certainty-threshold",
  makeCodeCodeView: "makecode-code-view",
  editInMakeCodeButton: "edit-in-makecode-button",
};

const LiveGraphStep = () => {
  const intl = useIntl();
  return (
    <HStack gap={5}>
      <Text>
        <FormattedMessage id="tour-dataSamples-liveGraph-content" />
      </Text>
      <AspectRatio w="150px" ratio={500 / 482} flexShrink={0}>
        <Image
          src={accelerometerImage}
          alt={intl.formatMessage({ id: "accelerometer-image-alt" })}
        />
      </AspectRatio>
    </HStack>
  );
};

const MakeCodeStep = () => {
  const intl = useIntl();
  return (
    <Stack gap={5}>
      <Text>
        <FormattedMessage id="tour-makecode-intro-content1" />
      </Text>
      <HStack gap={4}>
        <Icon as={RiInformationLine} css={{ width: 6, height: 6 }} />
        <Text w="fit-content">
          <FormattedMessage id="tour-makecode-intro-content2" />
          <br />
          <FormattedMessage id="tour-makecode-intro-content3" />
        </Text>
        <AspectRatio w="50px" ratio={1} flexShrink={0} mx={3}>
          <Image
            src={makecodeBackImage}
            borderRadius="sm"
            alt={intl.formatMessage({ id: "makecode-back-alt" })}
          />
        </AspectRatio>
      </HStack>
    </Stack>
  );
};

const classSelector = (classname: string) => `.${classname}`;

interface TourSpec {
  steps: TourStep[];
  markCompleted: TourTriggerName[];
}

export const getTour = (
  trigger: TourTrigger,
  actions: ActionData[]
): TourSpec => {
  const hasDataSamples = actions.some((a) => a.recordings.length > 0);
  switch (trigger.name) {
    case "Connect": {
      return {
        steps: [
          {
            title: <FormattedMessage id="tour-dataSamples-connected-title" />,
            content: (
              <FormattedMessageStepContent id="tour-dataSamples-connected-content" />
            ),
            modalSize: "lg",
          },
          {
            selector: classSelector(tourElClassname.liveGraph),
            title: <FormattedMessage id="live-data-graph" />,
            content: <LiveGraphStep />,
            spotlightStyle: { padding: 0 },
          },
          {
            selector: classSelector(tourElClassname.dataSamplesActionCard),
            title: <FormattedMessage id="actions-label" />,
            content: (
              <Text>
                <FormattedMessage id="tour-dataSamples-actions-common-content" />{" "}
                {!hasDataSamples && (
                  <FormattedMessage id="tour-dataSamples-actions-noRecordings-content" />
                )}
              </Text>
            ),
          },
          ...(hasDataSamples ? createCommonDataSamplesSteps(true) : []),
        ],
        markCompleted: hasDataSamples
          ? ["Connect", "DataSamplesRecorded"]
          : ["Connect"],
      };
    }
    case "DataSamplesRecorded": {
      return {
        markCompleted: ["DataSamplesRecorded"],
        steps: [
          ...[
            {
              title: (
                <FormattedMessage
                  id="tour-collect-afterFirst-title"
                  values={{ recordingCount: trigger.recordingCount }}
                />
              ),
              content: (
                <FormattedMessageStepContent id="tour-collect-afterFirst-content" />
              ),
            },
          ],
          ...createCommonDataSamplesSteps(
            actions.some((a) => a.recordings.length > 1)
          ),
        ],
      };
    }
    case "TrainModel": {
      return {
        markCompleted: ["TrainModel"],
        steps: [
          {
            title: (
              <FormattedMessage
                id={
                  trigger.delayedUntilConnection
                    ? "tour-trainModel-afterTrain-delayedUntilConnected-title"
                    : "tour-trainModel-afterTrain-alreadyConnected-title"
                }
              />
            ),
            content: (
              <FormattedMessageStepContent id="tour-trainModel-afterTrain-content" />
            ),
          },
          {
            title: <FormattedMessage id="estimated-action-label" />,
            content: (
              <FormattedMessageStepContent id="tour-trainModel-estimatedAction-content" />
            ),
            selector: classSelector(tourElClassname.estimatedAction),
            spotlightStyle: {
              paddingLeft: 8,
              paddingRight: -8,
              paddingTop: -8,
              paddingBottom: -8,
            },
          },
          {
            title: (
              <FormattedMessage id="tour-trainModel-certaintyRecognition-title" />
            ),
            content: (
              <FormattedMessageStepContent id="tour-trainModel-certaintyRecognition-content" />
            ),
            selector: classSelector(tourElClassname.certaintyThreshold),
          },
          {
            title: (
              <FormattedMessage id="tour-trainModel-makeCodeBlocks-title" />
            ),
            content: (
              <FormattedMessageStepContent id="tour-trainModel-makeCodeBlocks-content" />
            ),
            selector: classSelector(tourElClassname.makeCodeCodeView),
            placement: "bottom",
          },
          {
            title: <FormattedMessage id="edit-in-makecode-action" />,
            content: (
              <FormattedMessageStepContent id="tour-trainModel-editInMakeCode-content" />
            ),
            selector: classSelector(tourElClassname.editInMakeCodeButton),
          },
        ],
      };
    }
    case "MakeCode": {
      return {
        markCompleted: ["MakeCode"],
        steps: [
          {
            title: <FormattedMessage id="tour-makecode-intro-title" />,
            content: <MakeCodeStep />,
          },
        ],
      };
    }
    default:
      throw new Error(trigger);
  }
};

const createCommonDataSamplesSteps = (hasPreExistingRecordings: boolean) => {
  return [
    {
      selector: classSelector(tourElClassname.recordDataSamplesCard),
      title: <FormattedMessage id="tour-collect-collectMore-title" />,
      content: (
        <Text>
          <FormattedMessage
            id={
              hasPreExistingRecordings
                ? "tour-collect-collectMore-hasRecordings-content"
                : "tour-collect-collectMore-noRecordings-content"
            }
          />{" "}
          <FormattedMessage id="tour-collect-collectMore-explanation-content" />
        </Text>
      ),
    },
    {
      selector: classSelector(tourElClassname.addActionButton),
      title: <FormattedMessage id="tour-collect-addActions-title" />,
      content: (
        <FormattedMessageStepContent id="tour-collect-addActions-content" />
      ),
    },
    {
      selector: classSelector(tourElClassname.trainModelButton),
      title: <FormattedMessage id="train-model" />,
      content: (
        <FormattedMessageStepContent id="tour-collect-trainModel-content" />
      ),
    },
  ];
};
